<template>
  <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-900/5 tw-p-4">
    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
      <div class="tw-flex tw-items-center tw-gap-2">
        <font-awesome-icon icon="building" class="tw-h-4 tw-w-4 tw-text-slate-500" />
        <h4 class="tw-text-base tw-font-semibold tw-text-slate-900">Billing Customer</h4>
        <CopyButton
          v-if="billingCopyValue"
          :value="billingCopyValue"
          title="Copy billing customer"
          ariaLabel="Copy billing customer"
        />
      </div>

      <button
        v-if="editable"
        type="button"
        class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
        @click="toggleEditingBillingCustomer"
        title="Edit billing customer"
      >
        <font-awesome-icon icon="pen-to-square" />
      </button>
    </div>
    <div v-if="billTo">
      <div class="tw-mt-3 tw-text-sm tw-text-slate-700">
        <div class="tw-font-medium tw-text-slate-900">{{ billTo.name }}</div>
        <div v-if="billTo.billingAddress && billTo.billingAddress.streetAddress">
          {{ addressLine2(billTo.billingAddress) }}
        </div>
        <div>{{ addressLine3(billTo.billingAddress) }}</div>
      </div>

      <div v-if="isEditingCustomer" class="uk-flex uk-width-1-1">
        <EditCustomer
          :receiving="false"
          :orderId="orderId"
          :currentCustomerId="billTo && billTo.id"
          :currentCustomer="billTo"
          @saveCustomer="saveCustomer"
          @cancelCustomerEdit="cancelEditingBillingCustomer"
        >
        </EditCustomer>
      </div>

      <div v-if="isAddingContact" class="tw-mt-3 tw-w-full sm:tw-max-w-sm">
        <div
          class="tw-flex tw-h-10 tw-w-full tw-items-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-backdrop-blur"
        >
          <div class="tw-min-w-0 tw-flex-1">
            <filterable-dropdown
              class="tw-w-full tw-min-w-0"
              v-model="selectedBillingContactId"
              :options="billTo.contacts"
              label="name"
              :reduce="(c) => c.id"
              :inPill="true"
              @input="setContact"
            ></filterable-dropdown>
          </div>
          <div class="tw-my-2 tw-w-px tw-bg-slate-200/70"></div>
          <button
            type="button"
            class="tw-inline-flex tw-h-10 tw-shrink-0 tw-items-center tw-justify-center tw-px-3 tw-text-slate-700 hover:tw-bg-white"
            title="Cancel"
            aria-label="Cancel"
            @click="toggleAddContact"
          >
            <font-awesome-icon icon="xmark" />
          </button>
        </div>
      </div>
      <div v-else-if="billingContact">
        <p class="uk-margin-remove-left uk-margin-remove-bottom uk-margin-top">
          {{ billingContact.name }}
        </p>
        <p class="uk-margin-remove" v-if="billingContact.email">
          {{ billingContact.email }}
        </p>
        <p class="uk-margin-remove" v-if="billingContact.phoneNumber">
          {{ billingContact.phoneNumber }}
        </p>
      </div>
      <button
        v-else-if="showEditContact"
        type="button"
        class="tw-mt-3 tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
        @click="toggleAddContact"
        title="Add contact"
        aria-label="Add contact"
      >
        <font-awesome-icon icon="plus" />
        <span>Add Contact</span>
      </button>
      <Button
        v-if="showDetails"
        @click="showCustomerDetails"
        :isDefault="true"
        class="uk-margin-small-top uk-margin-small-left"
        >Details</Button
      >
    </div>
  </div>
  <CustomerDetails
    v-if="isCustomerDetailsOpen"
    v-model:isOpen="isCustomerDetailsOpen"
    :customerId="billTo?.id"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import BillingCustomersQuery from '@/gql/customers/BillingCustomers.gql';
import { addressMixin } from '../../../utils/address';
import Button from '../../core/Button.vue';
import CustomerDetails from '../../Customer/CustomerDetails.vue';
import FilterableDropdown from '../../core/FilterableDropdown.vue';
import UpdateBillingContactMutation from '../../../gql/orders/mutations/UpdateBillingContact.gql';
import EditCustomer from './EditCustomer.vue';

import CopyButton from '@/components/core/CopyButton.vue';

export default defineComponent({
  props: {
    billTo: {
      type: Object,
    },
    editable: {
      default: true,
    },
    orderId: {
      required: true,
    },
    billingContact: {
      type: Object,
    },
    showDetails: {
      default: true,
    },
    showEditContact: {
      default: true,
    },
  },
  mixins: [addressMixin],
  components: {
    FilterableDropdown,
    Button,
    EditCustomer,
    CopyButton,
    CustomerDetails,
  },
  data() {
    return {
      isAddingContact: false,
      isEditingCustomer: false,
      isCustomerDetailsOpen: false,
      selectedBillingContactId: null as any,
      selectedCustomer: null,
      customerNameQuery: '',
    };
  },
  watch: {
    billingContact: {
      immediate: true,
      handler() {
        const vm = this as any;
        const current = vm.billingContact;
        if (!current || !current.id) {
          vm.selectedBillingContactId = null;
          return;
        }

        vm.selectedBillingContactId = current.id;
      },
    },
    billTo: {
      deep: false,
      handler() {
        const vm = this as any;
        const current = vm.billingContact;
        if (!current || !current.id) return;
        vm.selectedBillingContactId = current.id;
      },
    },
    isAddingContact(val: boolean) {
      const vm = this as any;
      if (val) {
        const current = vm.billingContact;
        if (current && current.id) {
          vm.selectedBillingContactId = current.id;
        } else {
          vm.selectedBillingContactId = null;
        }
      }
    },
  },
  computed: {
    billingCopyValue(): string {
      const vm = this as any;
      const billTo = vm.billTo;
      if (!billTo) return '';

      const name = billTo.name || '';
      const line2 = billTo.billingAddress ? vm.addressLine2(billTo.billingAddress) : '';
      const line3 = billTo.billingAddress ? vm.addressLine3(billTo.billingAddress) : '';

      return [name, line2, line3].filter(Boolean).join('\n');
    },
  },
  methods: {
    saveCustomer() {
      const vm = (this as any);

      vm.$emit('updateOrder');
      vm.toggleEditingBillingCustomer();
    },
    cancelEditingBillingCustomer() {
      (this as any).isEditingCustomer = false;
    },
    toggleEditingBillingCustomer() {
      const vm = (this as any);

      if (!vm.isEditingCustomer) {
        vm.isEditingCustomer = true;
        vm.selectedCustomer = null;
      } else {
        vm.isEditingCustomer = false;
        vm.selectedCustomer = null;
      }
    },
    showCustomerDetails() {
      const vm = (this as any);
      const customer = vm.billTo;
      if (!customer || customer.id === undefined || customer.id === null) {
        window.location.href = '/customers';
        return;
      }
      vm.isCustomerDetailsOpen = true;
    },
    setContact(contactId) {
      const vm = (this as any);

      if (!contactId) {
        vm.selectedBillingContactId = null;
        return;
      }

      if (vm.billingContact && vm.billingContact.id === contactId) {
        vm.isAddingContact = false;
        return;
      }

      const contact = (vm.billTo?.contacts || []).find((c: any) => c && c.id === contactId);
      if (!contact || !contact.id) {
        return;
      }

      vm.$apollo
        .mutate({
          mutation: UpdateBillingContactMutation,
          variables: {
            orderId: vm.orderId,
            contactId,
          },
        })
        .then((data) => {
          vm.isAddingContact = false;
          vm.$emit('updateOrder');
        })
        .catch((err) => {
          console.error(err);
        });
    },
    toggleAddContact() {
      const vm = (this as any);

      if (!vm.isAddingContact) {
        vm.isAddingContact = true;
      } else {
        vm.isAddingContact = false;
      }
    },
  },
  ...{
    apollo: {
      originCustomers: {
        query: BillingCustomersQuery,
        debounce: 100,
        variables() {
          return {
            name: (this as any).customerNameQuery,
          };
        },
        update: (data) => data.receivingCustomers,
      },
    },
  },
});
</script>